"use client";

import { useQuery } from "@tanstack/react-query";
import Link from "next/link";

import {
  type BetterStackStatus,
  getStatusColor,
  getStatusText,
} from "@/lib/betterstack-status-client";

interface StatusIndicatorProps {
  /**
   * Size variant of the indicator
   * - "sm": Small variant for inline use (contact page)
   * - "default": Default size for footer
   */
  size?: "sm" | "default";
  /**
   * Whether to show a link to the status page
   */
  showLink?: boolean;
  /**
   * Color variant for different backgrounds
   * - "default": Dark text on light/cream background
   * - "blue": Light text on blue background
   */
  variant?: "default" | "blue";
}

const UNKNOWN_COLOR = {
  bg: "bg-muted-foreground",
  pulse: "bg-muted-foreground",
};

export function StatusIndicator({
  size = "default",
  showLink = true,
  variant = "default",
}: StatusIndicatorProps) {
  const isBlue = variant === "blue";
  const { data: status, isPending } = useQuery({
    queryKey: ["better-stack-status"],
    queryFn: async (): Promise<BetterStackStatus> => {
      const response = await fetch("/api/status");
      if (!response.ok) {
        throw new Error(`status fetch failed: ${response.status}`);
      }
      return response.json() as Promise<BetterStackStatus>;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const colors = status ? getStatusColor(status.indicator) : UNKNOWN_COLOR;
  const text = status
    ? getStatusText(status.indicator)
    : isPending
      ? "Checking system status"
      : "Status unknown";

  const indicator = (
    <div className="flex items-center gap-2">
      {size === "sm" ? (
        <div
          className={`size-2 animate-pulse rounded-full ${colors.bg}`}
          aria-hidden="true"
        />
      ) : (
        <div className="relative flex size-3" aria-hidden="true">
          <span
            className={`absolute inset-0 block size-full rounded-full ${colors.pulse} duration-150`}
          />
          <span className={`relative m-auto block size-1 ${colors.bg}`} />
        </div>
      )}
      <span
        className={
          size === "sm"
            ? "text-muted-foreground text-xs"
            : isBlue
              ? "text-sm text-white/80"
              : "text-muted-foreground text-sm"
        }
      >
        {text}
      </span>
    </div>
  );

  if (showLink) {
    return (
      <Link
        href="https://status.terac.com"
        target="_blank"
        rel="noopener noreferrer"
        className="block w-fit"
        aria-label={`System status: ${text}. Click to view status page.`}
      >
        {indicator}
      </Link>
    );
  }

  return indicator;
}
